import { useState, type ReactNode } from "react";
import { useLocation } from "@tanstack/react-router";
import { Layers, Briefcase, Building2 } from "lucide-react";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { cardSurface } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import {
  chromeInventoryFilterMode,
  scopeKindFromIds,
  type ScopeKind,
} from "./scope-bar-chrome";
import { InventoryFilters } from "./InventoryFilters";
import { ScopePickDialog, type ScopePickTab } from "./ScopePickDialog";

// Scope chrome for the topbar: Kaikki resets kanta; Salkku / Kiinteistöt open
// the pick dialog. Hallitse lives in the user menu, not here.

export function ScopeBar() {
  const [pickTab, setPickTab] = useState<ScopePickTab | null>(null);
  const location = useLocation();
  const { buildingId, groupId, selectedBuilding, activeGroup, reset } =
    useInventoryFilter();

  const kind = scopeKindFromIds(buildingId, groupId);
  const filterMode = chromeInventoryFilterMode(buildingId, location.pathname);
  const selectionLabel =
    kind === "building"
      ? (selectedBuilding?.name ?? "Kiinteistö")
      : kind === "group"
        ? (activeGroup?.name ?? "Salkku")
        : null;

  function chooseKind(next: ScopeKind) {
    if (next === "all") {
      reset();
      return;
    }
    setPickTab(next === "group" ? "groups" : "buildings");
  }

  return (
    <div className="flex flex-col gap-2 min-w-0">
      {filterMode !== "hidden" && (
        <InventoryFilters hideStatusFacet={filterMode === "without-status"} />
      )}

      <div className="flex items-center gap-2 min-w-0">
        <div
          className={cn(
            cardSurface,
            "inline-flex items-center p-0.5 shrink-0",
          )}
          role="radiogroup"
          aria-label="Tarkastelun laajuus"
        >
          <KindButton
            active={kind === "all"}
            onClick={() => chooseKind("all")}
          >
            <Layers className="h-3.5 w-3.5" />
            Kaikki kiinteistöt
          </KindButton>
          <KindButton
            active={kind === "group"}
            onClick={() => chooseKind("group")}
          >
            <Briefcase className="h-3.5 w-3.5" />
            Salkku
          </KindButton>
          <KindButton
            active={kind === "building"}
            onClick={() => chooseKind("building")}
          >
            <Building2 className="h-3.5 w-3.5" />
            Kiinteistöt
          </KindButton>
        </div>

        {selectionLabel && (
          <button
            type="button"
            onClick={() =>
              setPickTab(kind === "group" ? "groups" : "buildings")
            }
            className={cn(
              cardSurface,
              "px-2.5 py-1.5 text-xs font-medium truncate max-w-[240px] hover:border-primary/40 transition",
            )}
            title="Vaihda valinta"
          >
            {selectionLabel}
          </button>
        )}
      </div>

      {pickTab && (
        <ScopePickDialog tab={pickTab} onClose={() => setPickTab(null)} />
      )}
    </div>
  );
}

function KindButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-[5px] px-2.5 py-1.5 text-xs font-medium transition",
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
